import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { HeadingLarge, DisplaySmall, BodyMedium, BodySmall, Caption } from '@/components/ui/typography';
import { 
  CreditCard, 
  Briefcase, 
  CheckCircle2,
  Circle,
  AlertTriangle,
  Plus,
  Zap
} from 'lucide-react';
import { OneTimeCreditPurchaseDialog } from './dialogs/OneTimeCreditPurchaseDialog';
import type { CompanyBillingData, CompanySubscriptionTier } from '@/types/billing';
import { format } from 'date-fns';
import { useTranslation } from '@/hooks/useTranslation';
import { toast } from 'sonner';

interface UnifiedPlanUsageProps {
  companyData: CompanyBillingData;
  subscriptionTiers: CompanySubscriptionTier[];
  onChangePlans: (companyData: CompanyBillingData) => void;
  onRefetchBilling?: () => Promise<void>;
  isActive?: boolean;
}

export function UnifiedPlanUsage({ 
  companyData, 
  subscriptionTiers,
  onChangePlans,
  onRefetchBilling,
  isActive = true
}: UnifiedPlanUsageProps) {
  const { company, subscription, tier, usage } = companyData;
  const { t } = useTranslation('pages');
  const { t: tUI } = useTranslation('ui');
  const { t: tToast } = useTranslation('toast');
  const [showCreditPurchaseDialog, setShowCreditPurchaseDialog] = useState(false);

  // Calculate usage data (skip expensive operations when not active)
  const availableCredits = usage?.available_credits || 0;
  const creditsAcquired = usage?.credits_acquired || 0;
  const currentMonthUsed = usage?.current_month_credits_used || 0;
  const currentMonthStarting = usage?.current_month_starting_credits || 0;
  
  // Calculate monthly credits usage percentage
  // Formula: (current month used) / (current month used + available credits)
  const creditsUsagePercent = (currentMonthUsed + availableCredits) > 0
    ? Math.min(100, (currentMonthUsed / (currentMonthUsed + availableCredits)) * 100)
    : 0;
  
  const jobPostingsUsed = usage?.job_postings_used || 0;
  const jobPostingLimit = tier?.job_posting_limit;

  // Calculate warning threshold: 20% of monthly credits or at least 10 credits
  const monthlyCredits = tier?.monthly_credits || 0;
  const warningThreshold = Math.max(Math.round(monthlyCredits * 0.2), 10);
  const isCreditsDepleted = availableCredits === 0;
  const isCreditsLow = availableCredits > 0 && availableCredits <= warningThreshold;

  // Show toast notification for low credits when component becomes active
  useEffect(() => {
    if (!isActive) return;
    
    // Use localStorage to prevent showing the same notification multiple times per day
    const getCreditNotificationKey = (type: string) => 
      `credit_notification_${type}_${new Date().toDateString()}`;
    
    const depletedKey = getCreditNotificationKey('depleted');
    const lowKey = getCreditNotificationKey('low');
    
    if (isCreditsDepleted && !localStorage.getItem(depletedKey)) {
      toast.error(tToast('credits.depleted_title'), {
        description: tToast('credits.depleted_description'),
        duration: 8000,
        action: {
          label: tToast('credits.depleted_action'),
          onClick: () => setShowCreditPurchaseDialog(true)
        }
      });
      localStorage.setItem(depletedKey, 'true');
    } else if (isCreditsLow && !localStorage.getItem(lowKey)) {
      toast.warning(tToast('credits.low_title'), {
        description: tToast('credits.low_description', { balance: availableCredits }),
        duration: 6000,
        action: {
          label: tToast('credits.low_action'),
          onClick: () => setShowCreditPurchaseDialog(true)
        }
      });
      localStorage.setItem(lowKey, 'true');
    }
  }, [isActive, isCreditsDepleted, isCreditsLow, availableCredits, tToast]);

  // Format renewal/expiration date (memoize to avoid unnecessary calculations)
  const formatRenewalDate = () => {
    if (!isActive) return t('billing.status_active');
    if (!subscription) return t('billing.status_active');

    const isTemporaryPlan = subscription.is_temporary_plan;
    const expirationDate = subscription.plan_expiration_date;
    const nextBillingDate = subscription.current_period_end;

    if (isTemporaryPlan && expirationDate) {
      const date = new Date(expirationDate);
      return t('billing.expires_on', { date: format(date, 'MMM d') });
    } else if (nextBillingDate) {
      const date = new Date(nextBillingDate);
      return t('billing.renews_on', { date: format(date, 'MMM d') });
    }

    return t('billing.status_active');
  };

  // Format next credit allocation date
  const getNextAllocationDate = () => {
    if (!isActive || !subscription?.current_period_end) return null;
    const resetDate = new Date(subscription.current_period_end);
    return format(resetDate, 'MMM d');
  };

  return (
    <div className="space-y-6">
      {/* Three-Card Layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {/* Left Card: Plan Information */}
        <Card className="bg-muted/30">
          <CardContent className="space-y-6">
            {/* Gradient Icon */}
            <div className="flex items-start gap-3 md:gap-4">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <HeadingLarge className="break-words">
                    {tier?.tier_name || 'Free'} {t('billing.plan_suffix')}
                  </HeadingLarge>
                  {subscription?.is_temporary_plan && 
                   subscription?.plan_expiration_reason === 'welcome_gift' && (
                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                      <Zap className="h-3 w-3 mr-1" />
                      {t('billing.welcome_gift_badge')}
                    </Badge>
                  )}
                </div>
                <BodyMedium className="text-muted-foreground mt-1">
                  {formatRenewalDate()}
                </BodyMedium>
              </div>
            </div>

            {/* Manage Button */}
            <Button 
              variant="outline" 
              className="w-full md:w-auto px-8"
              onClick={() => onChangePlans(companyData)}
            >
              {t('billing.manage_button')}
            </Button>
            
            {/* Welcome Gift Expiration Warning */}
            {subscription?.plan_expiration_date && 
             subscription?.plan_expiration_reason === 'welcome_gift' && (
              <Alert className="mt-4 border-amber-200 bg-amber-50 dark:bg-amber-950/20">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 dark:text-amber-200">
                  <p className="font-medium">
                    {t('billing.welcome_gift_badge')} {formatRenewalDate().toLowerCase()}
                  </p>
                  <p className="text-xs mt-1">
                    {t('billing.welcome_gift_expiration_warning')}
                  </p>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Right Card: Credits Overview */}
        <Card className="bg-muted/30">
          <CardContent className="space-y-4">
            {/* Credit Warning Banners */}
            {isCreditsDepleted && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{tToast('credits.banner_depleted_title')}</AlertTitle>
                <AlertDescription>
                  {tToast('credits.banner_depleted_description')}
                  <button 
                    className="ml-2 text-sm underline font-medium hover:opacity-80"
                    onClick={() => setShowCreditPurchaseDialog(true)}
                  >
                    {tToast('credits.banner_depleted_action')}
                  </button>
                </AlertDescription>
              </Alert>
            )}
            
            {isCreditsLow && !isCreditsDepleted && (
              <Alert className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <AlertTitle className="text-orange-900 dark:text-orange-400">
                  {tToast('credits.banner_low_title')}
                </AlertTitle>
                <AlertDescription className="text-orange-800 dark:text-orange-300">
                  {tToast('credits.banner_low_description', { balance: availableCredits })}
                  <button 
                    className="ml-2 text-sm underline font-medium hover:opacity-80"
                    onClick={() => setShowCreditPurchaseDialog(true)}
                  >
                    {tToast('credits.banner_low_action')}
                  </button>
                </AlertDescription>
              </Alert>
            )}
            
            {/* Credits Header */}
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-lg font-semibold">{t('billing.credits_usage_title')}</h3>
              <div className="flex flex-col items-end gap-2">
                <DisplaySmall className="whitespace-nowrap">
                  {currentMonthUsed.toLocaleString()} {t('billing.of')} {currentMonthStarting.toLocaleString()}
                </DisplaySmall>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowCreditPurchaseDialog(true)}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  {t('billing.add_credits_button')}
                </Button>
              </div>
            </div>

            {/* Progress Bar */}
            <Progress 
              value={creditsUsagePercent} 
              className="h-3"
            />
            
            {/* Monthly Usage Stats */}
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{t('billing.used_this_month', { count: currentMonthUsed })}</span>
              <span>{Math.round(creditsUsagePercent)}{t('billing.percent_used')}</span>
            </div>

            {/* Credit Info Lines */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                <BodySmall className="text-muted-foreground">
                  {t('billing.credits_rollover_info')}
                </BodySmall>
              </div>
              {tier && tier.monthly_credits > 0 && (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <BodySmall className="text-muted-foreground">
                    {t('billing.next_credit_allocation', { 
                      credits: tier.monthly_credits.toLocaleString(), 
                      date: getNextAllocationDate() || 'N/A' 
                    })}
                  </BodySmall>
                </div>
              )}
            </div>

            {/* Credit Source Indicator */}
            <div className="flex items-center gap-2 pt-2">
              <Circle className="h-2 w-2 fill-primary text-primary flex-shrink-0" />
              <Caption className="text-muted-foreground">{t('billing.using_plan_allocation')}</Caption>
            </div>
          </CardContent>
        </Card>

        {/* Job Postings Card */}
        <Card className="bg-muted/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-muted-foreground" />
              <span className="text-lg font-semibold">{t('billing.job_postings_title')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <BodySmall className="text-muted-foreground">{t('billing.used_label')}</BodySmall>
              <DisplaySmall>
                {jobPostingsUsed}{jobPostingLimit ? ` / ${jobPostingLimit}` : ''}
              </DisplaySmall>
            </div>
            {jobPostingLimit && (
              <Progress 
                value={(jobPostingsUsed / jobPostingLimit) * 100} 
                className="h-3"
              />
            )}
            {!jobPostingLimit && (
              <Caption className="text-muted-foreground">{t('billing.unlimited_job_postings_label')}</Caption>
            )}
          </CardContent>
        </Card>
      </div>

      {/* One-Time Credit Purchase Dialog */}
      <OneTimeCreditPurchaseDialog
        open={showCreditPurchaseDialog}
        onOpenChange={setShowCreditPurchaseDialog}
        companyId={company.id}
        companyName={company.name}
        creditPrice={tier?.credit_price || 100}
        currentBalance={availableCredits}
        onSuccess={async () => {
          setShowCreditPurchaseDialog(false);
          await onRefetchBilling?.();
        }}
      />
    </div>
  );
}
